import {
  type ApiKeyAutoModelNamespaceRecord,
  type ApiKeyModelMappingConflict,
  type ApiKeyModelMappingRecord,
  type ApiKeySummary,
  type CatalogSku,
  type CreateApiKeyRequest,
  IP_ALLOWLIST_MAX_ENTRIES,
  IP_ALLOWLIST_MAX_ENTRY_LENGTH,
  parseIpAllowlistEntry,
  type UpdateApiKeyRequest,
} from '@nadoutong/contracts';
import { Check, Copy, Plus } from 'lucide-react';
import type { FormEvent } from 'react';
import { useMemo, useState } from 'react';
import { getGatewayBaseUrl } from '../api/client.ts';
import { localizeApiError, localizeCaughtError } from '../api/errorMessages.ts';
import { ModelMappingConflictError } from '../api/keyModelMappings.ts';
import { useDisplayRate } from '../api/useBilling.ts';
import { useCatalog } from '../api/useCatalog.ts';
import {
  useApplyKeyModelNamespace,
  useDeleteKeyModelMapping,
  useDisableKeyAutoModelNamespace,
  useEnableKeyAutoModelNamespace,
  useKeyModelMappings,
  useUpsertKeyModelMapping,
} from '../api/useKeyModelMappings.ts';
import { useCreateApiKey, useKeysList, useRevokeApiKey, useUpdateApiKey } from '../api/useKeys.ts';
import { apiKeyDisplayName, apiKeyRevokePhrase } from '../components/apiKeyName.ts';
import { formatTimestamp, formatUsdCentsAsCny } from '../components/formatters.ts';
import { Modal } from '../components/Modal.tsx';
import {
  formatLimitInputValue,
  isValidDisplayRate,
  parseSpendLimitInput,
} from '../components/spendLimit.ts';
import type { SpendLimitParseCode } from '../components/spendLimit.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { useToast } from '../components/Toast.tsx';
import { clipboardErrorKey, useClipboard } from '../hooks/useClipboard.tsx';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import type { I18nKey } from '../i18n/index.ts';
import { useT } from '../i18n/index.ts';
import { Button, ConfirmDialog, Select, Table, TBody, Td, THead, Th, Tr } from '../ui/index.ts';

const INPUT_CLASS =
  'h-9 w-full rounded border border-line bg-raised px-3 text-body text-ink placeholder:text-ink-faint transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60';

const KEY_LIMIT_ERROR_KEY: Readonly<Record<SpendLimitParseCode, I18nKey>> = {
  invalid: 'keys.limit_error.invalid',
  zero: 'keys.limit_error.zero',
  rate_unavailable: 'keys.limit_error.rate_unavailable',
};

/** `INPUT_CLASS` minus the fixed height, for the one-entry-per-line allowlist textarea. */
const TEXTAREA_CLASS =
  'w-full rounded border border-line bg-raised px-3 py-2 font-mono text-caption text-ink placeholder:text-ink-faint transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60';

/**
 * Spend caps offered by this console: day, week, month (epic #1 PR-10,
 * #18). The per-request cap that the API still accepts is deliberately
 * absent — storage-autonomy P3 (#1410 §5.4) retired it from both
 * consoles because nothing estimates a request's cost before it runs,
 * so there is no moment at which it could fire. The API field and any
 * stored value are untouched; a PATCH that omits it leaves the column
 * alone.
 */
type KeyLimitPeriod = 'daily' | 'weekly' | 'monthly';

type KeyLimitDraft = Record<KeyLimitPeriod, string>;
type KeyLimitUsdCents = Record<KeyLimitPeriod, number | null>;

const KEY_LIMIT_PERIODS: readonly {
  readonly id: KeyLimitPeriod;
  readonly labelKey: I18nKey;
  readonly placeholderKey: I18nKey;
}[] = [
  {
    id: 'daily',
    labelKey: 'keys.limit.daily.label',
    placeholderKey: 'keys.limit.daily.placeholder',
  },
  {
    id: 'weekly',
    labelKey: 'keys.limit.weekly.label',
    placeholderKey: 'keys.limit.weekly.placeholder',
  },
  {
    id: 'monthly',
    labelKey: 'keys.limit.monthly.label',
    placeholderKey: 'keys.limit.monthly.placeholder',
  },
];

function emptyLimitDraft(): KeyLimitDraft {
  return { daily: '', weekly: '', monthly: '' };
}

function keyLimitValue(key: ApiKeySummary, period: KeyLimitPeriod): number | null {
  if (period === 'daily') return key.dailyLimitUsdCents;
  if (period === 'weekly') return key.weeklyLimitUsdCents;
  return key.monthlyLimitUsdCents;
}

function limitDraftFromKey(
  key: ApiKeySummary,
  cnyPerUsd: number | null | undefined,
): KeyLimitDraft {
  return {
    daily: formatLimitInputValue(key.dailyLimitUsdCents, cnyPerUsd),
    weekly: formatLimitInputValue(key.weeklyLimitUsdCents, cnyPerUsd),
    monthly: formatLimitInputValue(key.monthlyLimitUsdCents, cnyPerUsd),
  };
}

function parseLimitInput(
  raw: string,
  cnyPerUsd: number | null | undefined,
  t: ReturnType<typeof useT>,
): { ok: true; value: number | null } | { ok: false; message: string } {
  const parsed = parseSpendLimitInput(raw, cnyPerUsd);
  if (!parsed.ok) {
    return { ok: false, message: t(KEY_LIMIT_ERROR_KEY[parsed.code]) };
  }
  return { ok: true, value: parsed.usdCents };
}

function parseLimitDraft(
  draft: KeyLimitDraft,
  cnyPerUsd: number | null | undefined,
  t: ReturnType<typeof useT>,
): { ok: true; value: KeyLimitUsdCents } | { ok: false; message: string } {
  const parsed: KeyLimitUsdCents = { daily: null, weekly: null, monthly: null };
  for (const period of KEY_LIMIT_PERIODS) {
    const result = parseLimitInput(draft[period.id], cnyPerUsd, t);
    if (!result.ok) return result;
    parsed[period.id] = result.value;
  }
  return { ok: true, value: parsed };
}

/**
 * Parse the IP-allowlist textarea — one IPv4/IPv6 address or CIDR per
 * line (#99). Validation is the SAME parser platform-api and the
 * gateway share (`@nadoutong/contracts` ip-allowlist, #1708), so an
 * entry accepted here is an entry the server accepts; the only thing
 * this wrapper adds is the line number, which the server's flat 400
 * message cannot give.
 *
 * The submitted entries are the parser's NORMALIZED spellings
 * (lowercased v6 expanded to 8 groups, v4-mapped folded, deduped) —
 * the same form the server stores and echoes back. Submitting them
 * pre-normalized keeps the edit round-trip stable: the prefill string,
 * the payload and the echo all agree, so the "did the user change
 * anything" string diff in `saveEdit` never sees a phantom change.
 */
function parseAllowlistDraft(
  raw: string,
  t: ReturnType<typeof useT>,
): { ok: true; value: string[] } | { ok: false; message: string } {
  const normalized: string[] = [];
  const seen = new Set<string>();
  let entryCount = 0;
  for (const [index, line] of raw.split('\n').entries()) {
    const entry = line.trim();
    if (entry.length === 0) continue;
    entryCount += 1;
    const parsed = parseIpAllowlistEntry(entry);
    if (parsed === null) {
      return {
        ok: false,
        message: t('keys.allowlist_error.invalid_entry', {
          line: index + 1,
          entry: entry.slice(0, IP_ALLOWLIST_MAX_ENTRY_LENGTH),
        }),
      };
    }
    if (!seen.has(parsed.normalized)) {
      seen.add(parsed.normalized);
      normalized.push(parsed.normalized);
    }
  }
  // Counted BEFORE dedup, like the server counts its raw array — a
  // 150-line paste is over the limit even when it dedups down to 50.
  if (entryCount > IP_ALLOWLIST_MAX_ENTRIES) {
    return {
      ok: false,
      message: t('keys.allowlist_error.too_many', { max: IP_ALLOWLIST_MAX_ENTRIES }),
    };
  }
  return { ok: true, value: normalized };
}

/**
 * Textarea prefill for a key's stored allowlist. The server echoes
 * normalized entries (`ApiKeySummary.ipAllowlist`); `null`, absent and
 * `[]` are all "unrestricted" and prefill as the empty string.
 */
function allowlistPrefillOf(key: ApiKeySummary): string {
  return (key.ipAllowlist ?? []).join('\n');
}

/**
 * Create omits a cleared cap, update sends it as an explicit `null`.
 * The asymmetry is deliberate: on POST there is no prior value to
 * clear, while on PATCH an omitted field means "leave alone", so
 * clearing a cap in the UI has to travel as `null`.
 */
function assignCreateLimitField(
  body: CreateApiKeyRequest,
  period: KeyLimitPeriod,
  value: number | null,
): void {
  if (value === null) return;
  if (period === 'daily') body.daily_limit_usd_cents = value;
  else if (period === 'weekly') body.weekly_limit_usd_cents = value;
  else body.monthly_limit_usd_cents = value;
}

function assignUpdateLimitField(
  body: UpdateApiKeyRequest,
  period: KeyLimitPeriod,
  value: number | null,
): void {
  if (period === 'daily') body.daily_limit_usd_cents = value;
  else if (period === 'weekly') body.weekly_limit_usd_cents = value;
  else body.monthly_limit_usd_cents = value;
}

/** Namespace-grouped view over the marketplace SKUs (epic #898 P6 #913). */
interface SkuNamespaceGroup {
  readonly namespaceId: string;
  readonly displayName: string;
  readonly skus: readonly CatalogSku[];
}

function groupSkusByNamespace(skus: readonly CatalogSku[]): readonly SkuNamespaceGroup[] {
  const groups = new Map<string, { displayName: string; skus: CatalogSku[] }>();
  for (const sku of skus) {
    const group = groups.get(sku.namespace_id) ?? {
      displayName: sku.namespace_display_name,
      skus: [],
    };
    group.skus.push(sku);
    groups.set(sku.namespace_id, group);
  }
  return [...groups.entries()]
    .map(([namespaceId, group]) => ({
      namespaceId,
      displayName: group.displayName,
      skus: [...group.skus].sort((a, b) => a.model.localeCompare(b.model)),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * Whether the signed-in caller owns this key, and may therefore RENAME,
 * cap or revoke it from this page.
 *
 * NOT a confidentiality boundary, and must not be read as one (#14).
 * What a caller is allowed to LEARN about the org's keys is decided by
 * platform-api, one layer down: `GET /v1/keys` sets `ownOnly =
 * !canReadOrgKeys` and appends `AND COALESCE(assigned_account_id,
 * created_by_account_id) = <caller>` for anyone without
 * `org:api_keys:read` (`routes/v1-keys.ts`). No member set carries that
 * permission, so a member's response never contains a colleague's key
 * to begin with. This predicate only ever narrows a MANAGER's
 * already-org-wide response, and it narrows it to the rows whose
 * rename/revoke controls that manager should be pointed at — not to the
 * rows they are cleared to see. Re-applying it to a count or a filter
 * list elsewhere would withhold nothing and desync those surfaces.
 *
 * `assignedAccountId` is nullable for keys minted before per-member
 * assignment existed; those legacy keys resolve to the org owner, so an
 * owner keeps seeing them and a member never does. Dropping that branch
 * would silently hide an owner's oldest keys.
 */
function isOwnApiKey(
  key: ApiKeySummary,
  currentAccountId: string | null,
  currentRole: string | null,
): boolean {
  if (currentAccountId === null) return false;
  if (key.assignedAccountId === currentAccountId) return true;
  return key.assignedAccountId === null && currentRole === 'owner';
}

/**
 * API key management surface.
 *
 * Layout:
 *   - Header with "Create key" button.
 *   - Table of existing keys (prefix + name + spend caps + IP allowlist +
 *     timestamps + inline rename + mappings + delete). User-facing history stays
 *     simple: revoked rows are treated as deleted and hidden, and rows
 *     belonging to other members are never listed — for a MANAGER the
 *     list endpoint answers org-wide, so this filter is what keeps a
 *     colleague's key off THIS page's rename and revoke buttons. (A
 *     member's response is already own-only; see `isOwnApiKey`.) It
 *     deliberately does not travel: `Requests.tsx` builds its key filter
 *     from the raw response and `Dashboard.tsx` counts it, both naming
 *     the scope in their copy instead of re-cutting the data (#14, #33).
 *   - Modal that surfaces the freshly-created plaintext key ONCE.
 *
 * Channel-less since epic #898 P6 (#913): keys no longer bind to a
 * channel. Model routing is per-key alias → SKU mappings managed in the
 * "Mappings" drawer, and creation can optionally seed those mappings
 * through one atomic server-side namespace snapshot. The key POST body
 * never carries channel_id.
 */

export function Keys(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const keysQ = useKeysList();
  const currentAccountId = user?.accountId ?? null;
  const ownActiveKeys = useMemo(
    () =>
      (keysQ.data?.keys ?? [])
        .filter((key) => key.revokedAt === null)
        .filter((key) => isOwnApiKey(key, currentAccountId, user?.orgRole ?? null)),
    [keysQ.data, currentAccountId, user?.orgRole],
  );
  const createM = useCreateApiKey();
  const applyNamespaceM = useApplyKeyModelNamespace();
  const toast = useToast();
  const canManageKeys =
    hasPermission(user, 'org:api_keys:manage') || hasPermission(user, 'member:api_keys:manage');
  const [createOpen, setCreateOpen] = useState(false);
  const [createdPlaintext, setCreatedPlaintext] = useState<{
    key: string;
    name: string | null;
  } | null>(null);

  return (
    <section data-testid="keys-page" className="flex flex-col gap-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2 className="font-display text-display text-ink">{t('keys.title')}</h2>
          <p className="text-body text-ink-low">{t('keys.subtitle')}</p>
        </div>
        {canManageKeys ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="keys-create-button"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-forest px-5 py-2.5 text-caption font-medium uppercase text-on-forest transition-all hover:bg-forest-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            <span>{t('keys.create')}</span>
          </button>
        ) : null}
      </header>

      <div className="flex flex-col gap-3 rounded-xl bg-raised px-5 py-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('keys.base_url.title')}
          </span>
          <p className="mt-1 text-caption text-ink-low">{t('keys.base_url.description')}</p>
          <code className="mt-2 block overflow-x-auto font-mono text-body text-forest">
            {getGatewayBaseUrl()}
          </code>
        </div>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(getGatewayBaseUrl());
            toast.success(t('keys.copied'));
          }}
          className="rounded p-1.5 text-ink-low hover:text-forest hover:bg-forest-tint transition-colors"
        >
          <Copy aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>

      <article className="flex flex-col gap-4 rounded-xl bg-card p-6">
        {keysQ.isLoading ? (
          <div className="flex flex-col gap-2" data-testid="keys-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
            <span className="sr-only">{t('keys.loading')}</span>
          </div>
        ) : keysQ.isError ? (
          <p className="text-body text-bad" role="alert" data-testid="keys-error">
            {localizeCaughtError(keysQ.error, t, 'keys.error')}
          </p>
        ) : keysQ.data !== undefined && ownActiveKeys.length === 0 ? (
          <p className="text-body text-ink-low" data-testid="keys-empty">
            {t('keys.empty')}
          </p>
        ) : keysQ.data !== undefined ? (
          <KeysTable keys={ownActiveKeys} canManage={canManageKeys} />
        ) : null}
      </article>

      {createOpen && canManageKeys ? (
        <CreateKeyModal
          isPending={createM.isPending || applyNamespaceM.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={async (name, limits, ipAllowlist, templateGroup) => {
            try {
              const body: CreateApiKeyRequest = {};
              if (name.length > 0) body.name = name;
              for (const period of KEY_LIMIT_PERIODS) {
                assignCreateLimitField(body, period.id, limits[period.id]);
              }
              // Same rule as a blank cap: on POST there is nothing to
              // clear, so an empty list is an ABSENT field, not `[]`.
              if (ipAllowlist.length > 0) body.ip_allowlist = ipAllowlist;
              const result = await createM.mutateAsync(body);
              setCreateOpen(false);
              setCreatedPlaintext({ key: result.key, name: result.summary.name });
              if (templateGroup !== null) {
                try {
                  await applyNamespaceM.mutateAsync({
                    keyId: result.summary.id,
                    namespaceId: templateGroup.namespaceId,
                  });
                } catch (err) {
                  const message = localizeCaughtError(err, t, 'error_banner.unexpected');
                  toast.error(t('keys.template.failure', { error: message }));
                }
              }
              return null;
            } catch (err) {
              const message = localizeApiError(err, t);
              toast.error(message);
              return message;
            }
          }}
        />
      ) : null}

      {createdPlaintext !== null ? (
        <PlaintextReveal
          plaintext={createdPlaintext.key}
          keyName={createdPlaintext.name}
          onClose={() => setCreatedPlaintext(null)}
        />
      ) : null}
    </section>
  );
}

interface KeysTableProps {
  readonly keys: readonly ApiKeySummary[];
  readonly canManage: boolean;
}

function KeysTable({ keys, canManage }: KeysTableProps): JSX.Element {
  const t = useT();
  const displayRateQ = useDisplayRate();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const limitsEditable = isValidDisplayRate(cnyPerUsd);
  const updateM = useUpdateApiKey();
  const revokeM = useRevokeApiKey();
  const toast = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [limitDraftState, setLimitDraftState] = useState<KeyLimitDraft | null>(null);
  const [allowlistDraftState, setAllowlistDraftState] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(null);
  const [mappingsTarget, setMappingsTarget] = useState<ApiKeySummary | null>(null);

  const editingKey = keys.find((key) => key.id === editingId) ?? null;
  /*
   * Prefill stays reactive until the first keystroke: the display rate
   * can resolve AFTER the row is on screen, and a CNY prefill computed
   * from a missing rate is the empty string. Snapshotting it at click
   * time would then read as "the user cleared this cap" and PATCH a
   * `null` over a cap the user never touched.
   */
  const initialLimitDraft = useMemo<KeyLimitDraft>(
    () =>
      editingKey !== null && limitsEditable
        ? limitDraftFromKey(editingKey, cnyPerUsd)
        : emptyLimitDraft(),
    [editingKey, limitsEditable, cnyPerUsd],
  );
  const limitDraft = limitDraftState ?? initialLimitDraft;
  /*
   * Same reactive-prefill pattern as the caps, and for the same class
   * of reason: the untouched state must be "whatever the row holds",
   * so a save with zero keystrokes in the textarea can never emit an
   * `ip_allowlist` write.
   */
  const initialAllowlistDraft = useMemo<string>(
    () => (editingKey !== null ? allowlistPrefillOf(editingKey) : ''),
    [editingKey],
  );
  const allowlistDraft = allowlistDraftState ?? initialAllowlistDraft;

  function setLimit(period: KeyLimitPeriod, value: string): void {
    setRowError(null);
    setLimitDraftState({ ...limitDraft, [period]: value });
  }

  function resetEdit(): void {
    setEditingId(null);
    setDraftName('');
    setLimitDraftState(null);
    setAllowlistDraftState(null);
  }

  function startEdit(key: ApiKeySummary): void {
    setEditingId(key.id);
    setDraftName(key.name ?? '');
    setLimitDraftState(null);
    setAllowlistDraftState(null);
    setRowError(null);
  }

  async function saveEdit(key: ApiKeySummary): Promise<void> {
    setRowError(null);
    const name = draftName.trim();
    if (name.length === 0) {
      setRowError({ id: key.id, message: t('keys.row_error.name_required') });
      return;
    }
    const body: UpdateApiKeyRequest = { name };
    if (limitsEditable) {
      const limits = parseLimitDraft(limitDraft, cnyPerUsd, t);
      if (!limits.ok) {
        setRowError({ id: key.id, message: limits.message });
        return;
      }
      /*
       * String diff against the prefill, NOT a numeric diff against the
       * stored cents: CNY↔USD conversion rounds in both directions, so
       * a numeric comparison can report a change the user never made
       * and emit a phantom cap write.
       */
      for (const period of KEY_LIMIT_PERIODS) {
        if (limitDraft[period.id] !== initialLimitDraft[period.id]) {
          assignUpdateLimitField(body, period.id, limits.value[period.id]);
        }
      }
    }
    if (allowlistDraft !== initialAllowlistDraft) {
      const allowlist = parseAllowlistDraft(allowlistDraft, t);
      if (!allowlist.ok) {
        setRowError({ id: key.id, message: allowlist.message });
        return;
      }
      /*
       * Whole-list replacement on the wire — never incremental. An
       * emptied textarea is a CLEARED list; on PATCH that travels as
       * an explicit `null` (`[]` is contractually equivalent, `null`
       * matches the cap-clearing convention above), while an
       * untouched textarea omits the field entirely.
       */
      body.ip_allowlist = allowlist.value.length === 0 ? null : allowlist.value;
    }
    try {
      await updateM.mutateAsync({ id: key.id, body });
      resetEdit();
    } catch (err) {
      const message = localizeCaughtError(err, t, 'keys.row_error.network');
      setRowError({ id: key.id, message });
      toast.error(message);
    }
  }

  return (
    <>
      <Table testId="keys-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('keys.column.prefix')}</Th>
            <Th>{t('keys.column.name')}</Th>
            <Th>{t('keys.column.spend_limits')}</Th>
            <Th>{t('keys.column.ip_allowlist')}</Th>
            <Th>{t('keys.column.created')}</Th>
            <Th>{t('keys.column.last_used')}</Th>
            <Th className="text-right">{t('keys.column.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {keys.map((key) => {
            const isEditing = editingId === key.id;
            return (
              <Tr key={key.id} data-testid={`keys-row-${key.id}`}>
                {/*
                  The prefix is this console's first column, so it is the
                  cell that becomes the phone card's title. `name` is
                  nullable and would leave untitled cards.

                  Printed VERBATIM, with no trailing ellipsis: the same
                  prefix is what `/usage` and `/requests` show for a key
                  with no name, and a lone `…` here made the two read as
                  two different keys to anyone comparing tabs (#47). The
                  column header already says these are prefixes.
                */}
                <Td mono emphasis heading data-testid={`keys-prefix-${key.id}`}>
                  {key.prefix.length > 0 ? key.prefix : '—'}
                </Td>
                {/* Holds a wrapping edit form, so it owns the card width. */}
                <Td label={t('keys.column.name')} stack className="text-ink">
                  {isEditing ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="text"
                        value={draftName}
                        maxLength={120}
                        onChange={(e) => setDraftName(e.currentTarget.value)}
                        data-testid={`keys-edit-input-${key.id}`}
                        className={INPUT_CLASS}
                      />
                      {KEY_LIMIT_PERIODS.map((period) => (
                        <input
                          key={period.id}
                          type="text"
                          inputMode="decimal"
                          value={limitDraft[period.id]}
                          disabled={!limitsEditable}
                          onChange={(e) => setLimit(period.id, e.currentTarget.value)}
                          data-testid={`keys-edit-limit-${key.id}-${period.id}`}
                          aria-label={t(period.labelKey)}
                          placeholder={t(period.placeholderKey)}
                          className={`${INPUT_CLASS} w-28`}
                        />
                      ))}
                      {/*
                        Prefilled with the server's NORMALIZED echo —
                        which is why an untouched save can string-diff
                        against it — and cleared to say "unrestricted";
                        the hint underneath spells that out.
                      */}
                      <textarea
                        value={allowlistDraft}
                        rows={3}
                        disabled={updateM.isPending}
                        onChange={(e) => {
                          setRowError(null);
                          setAllowlistDraftState(e.currentTarget.value);
                        }}
                        data-testid={`keys-edit-allowlist-${key.id}`}
                        aria-label={t('keys.column.ip_allowlist')}
                        placeholder={t('keys.allowlist.placeholder')}
                        className={TEXTAREA_CLASS}
                      />
                      <span className="w-full text-mono-label leading-4 text-ink-low">
                        {t('keys.allowlist.hint')}
                      </span>
                      <button
                        type="button"
                        onClick={() => void saveEdit(key)}
                        disabled={updateM.isPending}
                        data-testid={`keys-edit-save-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-forest px-3 py-1 text-mono-label font-medium uppercase text-on-forest disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.save')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          resetEdit();
                          setRowError(null);
                        }}
                        disabled={updateM.isPending}
                        data-testid={`keys-edit-cancel-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-active px-3 py-1 text-mono-label font-medium uppercase text-ink disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.cancel')}
                      </button>
                    </div>
                  ) : (
                    /*
                      NOT `apiKeyDisplayName`, and this is the one place
                      that is true (#47). This cell is not a slot that
                      has to NAME the key — the prefix column beside it
                      already does that, with the same characters every
                      other page prints. This cell states the value of
                      the `name` FIELD, under a `Name` label the phone
                      card renders verbatim, next to the rename form
                      that is the only place in the console that can
                      set it. Falling back to the prefix here would put
                      `Name  sk-…` on a key that has no name, and would
                      erase the only signal an admin has for "these keys
                      still need naming" from the only page that can act
                      on it.
                    */
                    <span data-testid={`keys-name-${key.id}`}>{key.name ?? '—'}</span>
                  )}
                  {rowError !== null && rowError.id === key.id ? (
                    <p
                      className="mt-1 text-mono-label font-medium text-bad"
                      role="alert"
                      data-testid={`keys-edit-error-${key.id}`}
                    >
                      {rowError.message}
                    </p>
                  ) : null}
                </Td>
                <Td
                  label={t('keys.column.spend_limits')}
                  stack
                  data-testid={`keys-limit-${key.id}`}
                >
                  <KeyLimitCell keySummary={key} cnyPerUsd={cnyPerUsd} />
                </Td>
                <Td
                  label={t('keys.column.ip_allowlist')}
                  stack
                  data-testid={`keys-allowlist-${key.id}`}
                >
                  <KeyAllowlistCell keySummary={key} />
                </Td>
                <Td mono label={t('keys.column.created')}>
                  {formatTimestamp(key.createdAt)}
                </Td>
                <Td mono label={t('keys.column.last_used')}>
                  {key.lastUsedAt !== null ? formatTimestamp(key.lastUsedAt) : t('keys.never_used')}
                </Td>
                <Td label={t('keys.column.actions')} stack>
                  <div className="flex items-center justify-end gap-2 max-md:justify-start">
                    <button
                      type="button"
                      onClick={() => setMappingsTarget(key)}
                      data-testid={`keys-mappings-${key.id}`}
                      className="inline-flex items-center justify-center rounded-lg bg-active px-3 py-1 text-mono-label font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
                    >
                      {t('keys.mappings.button')}
                    </button>
                    {canManage && !isEditing ? (
                      <button
                        type="button"
                        onClick={() => startEdit(key)}
                        data-testid={`keys-rename-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-active px-3 py-1 text-mono-label font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.rename')}
                      </button>
                    ) : null}
                    {canManage ? (
                      <button
                        type="button"
                        onClick={() => setRevokeTarget(key)}
                        data-testid={`keys-revoke-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-bad-tint px-3 py-1 text-mono-label font-medium uppercase text-bad transition-all hover:brightness-110 disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.revoke')}
                      </button>
                    ) : null}
                  </div>
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>

      {mappingsTarget !== null ? (
        <KeyMappingsModal
          target={mappingsTarget}
          canManage={canManage}
          onClose={() => setMappingsTarget(null)}
        />
      ) : null}

      {revokeTarget !== null ? (
        <RevokeKeyModal
          target={revokeTarget}
          isPending={revokeM.isPending}
          onClose={() => setRevokeTarget(null)}
          onConfirm={async () => {
            try {
              await revokeM.mutateAsync(revokeTarget.id);
              setRevokeTarget(null);
              return null;
            } catch (err) {
              const message = localizeApiError(err, t);
              toast.error(message);
              return message;
            }
          }}
        />
      ) : null}
    </>
  );
}

/**
 * All three caps collapsed into ONE column. Widening the table to a
 * column per period would read fine on a desktop grid and fall apart
 * below `md`, where every `Td` turns into a labelled line of a card
 * (`src/ui/Table.tsx`) — eight lines per key, most of them "—".
 *
 * Only the caps that are actually set render, so an unset period has
 * no testid at all rather than a dash the reader has to decode.
 */
function KeyLimitCell({
  keySummary,
  cnyPerUsd,
}: {
  readonly keySummary: ApiKeySummary;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const set = KEY_LIMIT_PERIODS.filter((period) => keyLimitValue(keySummary, period.id) !== null);
  if (set.length === 0) {
    return <span className="text-ink-low">{t('keys.limit.none')}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {set.map((period) => {
        const value = keyLimitValue(keySummary, period.id);
        return (
          <span
            key={period.id}
            data-testid={`keys-limit-${keySummary.id}-${period.id}`}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-line-soft bg-well px-1.5 py-0.5 text-caption"
          >
            <span className="text-ink-low">{t(period.labelKey)}</span>
            <span className="font-mono tabular-nums text-ink-mid">
              {value === null ? '—' : formatUsdCentsAsCny(value, cnyPerUsd)}
            </span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Count badge, not the entries: a list may be 100 lines long, and below
 * `md` this cell becomes one line of the phone card. The full
 * normalized list is readable where it is editable — the row's edit
 * textarea — and rides along here as a native `title` for a desktop
 * hover. Unrestricted keys get muted copy, mirroring `keys.limit.none`
 * one column over.
 */
function KeyAllowlistCell({ keySummary }: { readonly keySummary: ApiKeySummary }): JSX.Element {
  const t = useT();
  const entries = keySummary.ipAllowlist ?? [];
  if (entries.length === 0) {
    return <span className="text-ink-low">{t('keys.allowlist.none')}</span>;
  }
  return (
    <span
      title={entries.join('\n')}
      data-testid={`keys-allowlist-badge-${keySummary.id}`}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-line-soft bg-well px-1.5 py-0.5 font-mono text-caption tabular-nums text-ink-mid"
    >
      {t('keys.allowlist.count', { count: entries.length })}
    </span>
  );
}

/**
 * Local calendar day as `YYYY-MM-DD`, for the default key name. Local
 * rather than UTC: the reader matches it against their own "I made this
 * yesterday", not against an ISO instant.
 */
function localDateStamp(now: Date): string {
  const year = String(now.getFullYear()).padStart(4, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

interface CreateKeyModalProps {
  readonly isPending: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (
    name: string,
    limits: KeyLimitUsdCents,
    ipAllowlist: string[],
    templateGroup: SkuNamespaceGroup | null,
  ) => Promise<string | null>;
}

function CreateKeyModal({ isPending, onClose, onSubmit }: CreateKeyModalProps): JSX.Element {
  const t = useT();
  /*
   * Prefilled, not blank. `name` is optional end to end — the contract
   * types it `name?: string` and platform-api simply stores NULL when
   * it is absent — so the dialog cannot refuse an empty submit without
   * changing what the product allows. What it CAN do is make sure the
   * one-click path does not mint an ANONYMOUS credential: a key created
   * by a stray click on an untouched dialog is otherwise indis-
   * tinguishable from every other unnamed key in the list, and the only
   * way back is to revoke by guesswork. Clearing the field is still the
   * way to get an unnamed key.
   */
  const [name, setName] = useState(() =>
    t('keys.create_modal.name_default', { date: localDateStamp(new Date()) }),
  );
  const [limitDraft, setLimitDraft] = useState<KeyLimitDraft>(emptyLimitDraft);
  const [allowlistDraft, setAllowlistDraft] = useState('');
  const [templateNamespace, setTemplateNamespace] = useState('');
  const [error, setError] = useState<string | null>(null);
  const catalogQ = useCatalog();
  const displayRateQ = useDisplayRate();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const limitsEditable = isValidDisplayRate(cnyPerUsd);

  const namespaceGroups = useMemo(
    () => groupSkusByNamespace(catalogQ.data?.skus ?? []),
    [catalogQ.data?.skus],
  );
  const selectedTemplateGroup =
    namespaceGroups.find((group) => group.namespaceId === templateNamespace) ?? null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    let limits: KeyLimitUsdCents = { daily: null, weekly: null, monthly: null };
    if (limitsEditable) {
      const parsed = parseLimitDraft(limitDraft, cnyPerUsd, t);
      if (!parsed.ok) {
        setError(parsed.message);
        return;
      }
      limits = parsed.value;
    }
    const allowlist = parseAllowlistDraft(allowlistDraft, t);
    if (!allowlist.ok) {
      setError(allowlist.message);
      return;
    }
    const message = await onSubmit(name.trim(), limits, allowlist.value, selectedTemplateGroup);
    if (message !== null) {
      setError(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isPending) onClose();
      }}
      titleId="create-key-modal-title"
      testId="keys-create-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="create-key-modal-title" className="text-heading text-ink">
          {t('keys.create_modal.title')}
        </h2>
        <p className="text-body text-ink-low">{t('keys.create_modal.description')}</p>
        <label className="flex flex-col gap-2">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('keys.create_modal.name_label')}
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            disabled={isPending}
            maxLength={120}
            data-testid="keys-create-name"
            placeholder={t('keys.create_modal.name_placeholder')}
            className={INPUT_CLASS}
          />
        </label>
        <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
          <legend className="p-0 font-mono text-mono-label uppercase text-ink-low">
            {t('keys.create_modal.limits_label')}
          </legend>
          <div className="grid grid-cols-3 gap-2 max-sm:grid-cols-1">
            {KEY_LIMIT_PERIODS.map((period) => (
              <label key={period.id} className="flex flex-col gap-1">
                <span className="text-caption text-ink-low">{t(period.labelKey)}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={limitDraft[period.id]}
                  onChange={(e) => {
                    const next = e.currentTarget.value;
                    setLimitDraft((current) => ({ ...current, [period.id]: next }));
                    setError(null);
                  }}
                  disabled={isPending || !limitsEditable}
                  data-testid={`keys-create-limit-${period.id}`}
                  placeholder={t(period.placeholderKey)}
                  className={INPUT_CLASS}
                />
              </label>
            ))}
          </div>
          {limitsEditable ? (
            <span
              className="text-mono-label leading-4 text-ink-low"
              data-testid="keys-create-limits-hint"
            >
              {t('keys.create_modal.limits_hint')}
            </span>
          ) : (
            <span
              className="text-mono-label leading-4 text-warn"
              role="status"
              data-testid="keys-create-rate-notice"
            >
              {displayRateQ.isLoading
                ? t('keys.limit_loading')
                : t('keys.limit_error.rate_unavailable')}
            </span>
          )}
        </fieldset>
        <label className="flex flex-col gap-2">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('keys.create_modal.allowlist_label')}
          </span>
          <textarea
            value={allowlistDraft}
            rows={3}
            onChange={(e) => {
              setAllowlistDraft(e.currentTarget.value);
              setError(null);
            }}
            disabled={isPending}
            data-testid="keys-create-allowlist"
            placeholder={t('keys.allowlist.placeholder')}
            className={TEXTAREA_CLASS}
          />
          <span
            className="text-mono-label leading-4 text-ink-low"
            data-testid="keys-create-allowlist-hint"
          >
            {t('keys.allowlist.hint')}
          </span>
        </label>
        <label className="flex flex-col gap-2">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('keys.create_modal.template_label')}
          </span>
          <select
            value={templateNamespace}
            onChange={(e) => {
              setTemplateNamespace(e.currentTarget.value);
              setError(null);
            }}
            disabled={isPending || catalogQ.isLoading || namespaceGroups.length === 0}
            data-testid="keys-create-template-namespace"
            className={INPUT_CLASS}
          >
            <option value="">{t('keys.create_modal.template_none')}</option>
            {namespaceGroups.map((group) => (
              <option key={group.namespaceId} value={group.namespaceId}>
                {group.displayName}
              </option>
            ))}
          </select>
          {selectedTemplateGroup !== null ? (
            <span
              className="text-mono-label leading-4 text-ink-low"
              data-testid="keys-create-template-hint"
            >
              {t('keys.create_modal.template_hint', {
                count: String(selectedTemplateGroup.skus.length),
              })}
            </span>
          ) : null}
        </label>
        {catalogQ.isError ? (
          <p className="text-caption text-ink-low" role="status">
            {t('keys.create_modal.catalog_error')}
          </p>
        ) : null}
        {error !== null ? (
          <p
            className="text-caption font-medium text-bad"
            role="alert"
            data-testid="keys-create-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="keys-create-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-caption font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('keys.create_modal.cancel')}
          </button>
          <button
            type="submit"
            disabled={isPending}
            data-testid="keys-create-submit"
            className="inline-flex items-center justify-center rounded-lg bg-forest px-4 py-2 text-caption font-medium uppercase text-on-forest transition-all hover:bg-forest-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {isPending ? t('keys.create_modal.submitting') : t('keys.create_modal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface KeyMappingsModalProps {
  readonly target: ApiKeySummary;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

interface NamespaceMappingPanelProps {
  readonly keyId: string;
  readonly canManage: boolean;
  readonly groups: readonly SkuNamespaceGroup[];
  readonly autoNamespaces: readonly ApiKeyAutoModelNamespaceRecord[];
  readonly catalogUnavailable: boolean;
  readonly namespaceOperationsEnabled: boolean;
}

function NamespaceMappingPanel({
  keyId,
  canManage,
  groups,
  autoNamespaces,
  catalogUnavailable,
  namespaceOperationsEnabled,
}: NamespaceMappingPanelProps): JSX.Element {
  const t = useT();
  const applyM = useApplyKeyModelNamespace();
  const enableAutoM = useEnableKeyAutoModelNamespace();
  const disableAutoM = useDisableKeyAutoModelNamespace();
  const [selectedNamespaceId, setSelectedNamespaceId] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [operationConflicts, setOperationConflicts] = useState<
    readonly ApiKeyModelMappingConflict[]
  >([]);
  const [disableTarget, setDisableTarget] = useState<ApiKeyAutoModelNamespaceRecord | null>(null);

  const pending = applyM.isPending || enableAutoM.isPending || disableAutoM.isPending;
  const selectedGroup = groups.find((group) => group.namespaceId === selectedNamespaceId) ?? null;
  const enabledIds = new Set(autoNamespaces.map((record) => record.namespace_id));

  function namespaceLabel(namespaceId: string, fallbackSlug: string | null): string {
    return (
      groups.find((group) => group.namespaceId === namespaceId)?.displayName ??
      fallbackSlug ??
      namespaceId
    );
  }

  function skuLabel(skuId: string, fallbackName: string): string {
    for (const group of groups) {
      const sku = group.skus.find((candidate) => candidate.sku_id === skuId);
      if (sku !== undefined) return sku.sku_name;
    }
    return fallbackName;
  }

  function sourceLabel(source: 'manual' | 'automatic'): string {
    return source === 'automatic'
      ? t('keys.mappings.source.automatic')
      : t('keys.mappings.source.manual');
  }

  function errorMessage(err: unknown): string {
    return localizeCaughtError(err, t, 'keys.row_error.network');
  }

  function handleOperationError(err: unknown): void {
    if (err instanceof ModelMappingConflictError) {
      setOperationConflicts(err.conflicts);
      setError(null);
      return;
    }
    setOperationConflicts([]);
    setError(errorMessage(err));
  }

  async function applyCurrentModels(): Promise<void> {
    if (selectedGroup === null) return;
    setError(null);
    setStatus(null);
    setOperationConflicts([]);
    try {
      const result = await applyM.mutateAsync({ keyId, namespaceId: selectedGroup.namespaceId });
      setStatus(
        t('keys.mappings.namespace.applied', {
          namespace: selectedGroup.displayName,
          count: String(result.applied_count),
        }),
      );
    } catch (err) {
      handleOperationError(err);
    }
  }

  async function enableAutomaticMapping(): Promise<void> {
    if (selectedGroup === null) return;
    setError(null);
    setStatus(null);
    setOperationConflicts([]);
    try {
      await enableAutoM.mutateAsync({ keyId, namespaceId: selectedGroup.namespaceId });
      setStatus(
        t('keys.mappings.namespace.auto_enabled', { namespace: selectedGroup.displayName }),
      );
    } catch (err) {
      handleOperationError(err);
    }
  }

  async function disableAutomaticMapping(record: ApiKeyAutoModelNamespaceRecord): Promise<void> {
    const label = namespaceLabel(record.namespace_id, record.namespace_slug);
    setError(null);
    setStatus(null);
    setOperationConflicts([]);
    try {
      await disableAutoM.mutateAsync({ keyId, namespaceId: record.namespace_id });
      setStatus(t('keys.mappings.namespace.auto_disabled', { namespace: label }));
      setDisableTarget(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <section
      aria-labelledby="keys-mapping-namespace-title"
      className="flex flex-col gap-4 rounded-lg border border-line bg-raised p-4"
      data-testid="keys-mapping-namespace-panel"
    >
      <div className="flex flex-col gap-1">
        <h3 id="keys-mapping-namespace-title" className="m-0 text-heading text-ink">
          {t('keys.mappings.namespace.title')}
        </h3>
        <p className="m-0 text-caption text-ink-low">{t('keys.mappings.namespace.description')}</p>
      </div>

      {canManage ? (
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-2">
            <span className="text-label font-medium text-ink-mid">
              {t('keys.mappings.namespace.select_label')}
            </span>
            <Select
              value={selectedNamespaceId}
              onValueChange={(value) => {
                setSelectedNamespaceId(value);
                setError(null);
                setStatus(null);
                setOperationConflicts([]);
              }}
              disabled={pending || catalogUnavailable || groups.length === 0}
              ariaLabel={t('keys.mappings.namespace.select_label')}
              testId="keys-mapping-namespace-action"
              placeholder={t('keys.mappings.namespace.select_placeholder')}
              options={groups.map((group) => ({
                value: group.namespaceId,
                label: group.displayName,
              }))}
            />
            {selectedGroup !== null ? (
              <span className="text-caption text-ink-low">
                {t('keys.mappings.namespace.selected_hint', {
                  count: String(selectedGroup.skus.length),
                })}
              </span>
            ) : null}
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              disabled={pending || selectedGroup === null || !namespaceOperationsEnabled}
              onClick={() => {
                void applyCurrentModels();
              }}
              data-testid="keys-mapping-namespace-apply"
            >
              {t('keys.mappings.namespace.apply')}
            </Button>
            <Button
              variant="primary"
              disabled={
                pending ||
                selectedGroup === null ||
                enabledIds.has(selectedNamespaceId) ||
                !namespaceOperationsEnabled
              }
              onClick={() => {
                void enableAutomaticMapping();
              }}
              data-testid="keys-mapping-namespace-auto-enable"
            >
              {enabledIds.has(selectedNamespaceId)
                ? t('keys.mappings.namespace.auto_enabled_button')
                : t('keys.mappings.namespace.auto_enable')}
            </Button>
          </div>
        </div>
      ) : (
        <p
          role="status"
          className="m-0 text-caption text-ink-mid"
          data-testid="keys-mappings-readonly"
        >
          {t('keys.mappings.readonly')}
        </p>
      )}

      {!namespaceOperationsEnabled ? (
        <p
          role="status"
          className="m-0 rounded border border-warn-line bg-warn-tint px-3 py-2 text-caption text-ink-mid"
          data-testid="keys-mapping-namespace-operations-disabled"
        >
          {t('keys.mappings.namespace.operations_disabled')}
        </p>
      ) : null}

      {operationConflicts.length > 0 ? (
        <section
          role="alert"
          className="flex flex-col gap-2 rounded border border-warn-line bg-warn-tint px-3 py-2"
          data-testid="keys-mapping-namespace-conflicts"
        >
          <p className="m-0 text-label font-medium text-warn">
            {t('keys.mappings.conflicts.title')}
          </p>
          <p className="m-0 text-caption text-ink-mid">
            {t('keys.mappings.conflicts.description')}
          </p>
          <ul className="m-0 list-none space-y-2 p-0">
            {operationConflicts.map((conflict) => (
              <li
                key={conflict.alias}
                className="rounded border border-warn-line bg-card px-3 py-2"
                data-testid={`keys-mapping-namespace-conflict-${conflict.alias}`}
              >
                <p className="m-0 font-mono text-mono text-ink">{conflict.alias}</p>
                <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-caption text-ink-mid">
                  {conflict.candidates.map((candidate) => (
                    <li key={`${candidate.namespace_id}:${candidate.sku_id}`}>
                      <span className="font-mono">
                        {skuLabel(candidate.sku_id, candidate.sku_name)}
                      </span>
                      {' · '}
                      {candidate.sources.map(sourceLabel).join(', ')}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="flex flex-col gap-2">
        <p className="m-0 text-label font-medium text-ink-mid">
          {t('keys.mappings.namespace.enabled_title')}
        </p>
        {autoNamespaces.length === 0 ? (
          <p className="m-0 text-caption text-ink-low" data-testid="keys-mapping-auto-empty">
            {t('keys.mappings.namespace.enabled_empty')}
          </p>
        ) : (
          <ul
            className="m-0 flex list-none flex-col gap-2 p-0"
            data-testid="keys-mapping-auto-list"
          >
            {autoNamespaces.map((record) => (
              <li
                key={record.namespace_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-line-soft bg-card px-3 py-2"
                data-testid={`keys-mapping-auto-${record.namespace_id}`}
              >
                <span className="font-mono text-mono text-ink">
                  {namespaceLabel(record.namespace_id, record.namespace_slug)}
                </span>
                {canManage ? (
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={pending}
                    onClick={() => setDisableTarget(record)}
                    data-testid={`keys-mapping-auto-disable-${record.namespace_id}`}
                  >
                    {t('keys.mappings.namespace.auto_disable')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {catalogUnavailable ? (
        <p role="alert" className="m-0 text-caption text-bad">
          {t('keys.mappings.catalog_error')}
        </p>
      ) : null}
      {error !== null ? (
        <p
          role="alert"
          className="m-0 text-caption text-bad"
          data-testid="keys-mapping-namespace-error"
        >
          {error}
        </p>
      ) : null}
      {status !== null ? (
        <p
          role="status"
          className="m-0 text-caption text-ok"
          data-testid="keys-mapping-namespace-status"
        >
          {status}
        </p>
      ) : null}

      {disableTarget !== null ? (
        <Modal
          isOpen
          onClose={() => {
            if (!disableAutoM.isPending) setDisableTarget(null);
          }}
          titleId="disable-auto-namespace-title"
          testId="keys-mapping-auto-disable-confirm"
        >
          <div className="flex flex-col gap-5 p-6">
            <div className="flex flex-col gap-2">
              <h2 id="disable-auto-namespace-title" className="m-0 text-heading text-ink">
                {t('keys.mappings.namespace.disable_title')}
              </h2>
              <p className="m-0 text-body text-ink-mid">
                {t('keys.mappings.namespace.disable_description', {
                  namespace: namespaceLabel(
                    disableTarget.namespace_id,
                    disableTarget.namespace_slug,
                  ),
                })}
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                disabled={disableAutoM.isPending}
                onClick={() => setDisableTarget(null)}
              >
                {t('keys.mappings.namespace.disable_cancel')}
              </Button>
              <Button
                variant="danger"
                disabled={disableAutoM.isPending}
                onClick={() => {
                  void disableAutomaticMapping(disableTarget);
                }}
                data-testid="keys-mapping-auto-disable-confirm-confirm"
              >
                {t('keys.mappings.namespace.disable_confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

/**
 * Per-key "Mappings" drawer (epic #898 P6 #913). Lists the key's
 * alias → SKU mappings, adds new ones (alias input + a namespace-grouped
 * SKU select fed by `/v1/catalog/me` `skus[]`), re-PUTs an existing
 * alias to change its target, and deletes.
 *
 * Alias validation renders inline next to the input — the platform
 * rejects aliases containing `/`, so the same rule is checked
 * client-side and any server-side alias rejection also lands on the
 * field, never toast-only.
 */
function KeyMappingsModal({ target, canManage, onClose }: KeyMappingsModalProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const mappingsQ = useKeyModelMappings(target.id);
  const catalogQ = useCatalog();
  const upsertM = useUpsertKeyModelMapping();
  const deleteM = useDeleteKeyModelMapping();

  const [editingAlias, setEditingAlias] = useState<string | null>(null);
  const [alias, setAlias] = useState('');
  const [skuId, setSkuId] = useState('');
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [skuError, setSkuError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [mutationConflicts, setMutationConflicts] = useState<readonly ApiKeyModelMappingConflict[]>(
    [],
  );
  const [deleteConflictAlias, setDeleteConflictAlias] = useState<string | null>(null);

  const pending = upsertM.isPending || deleteM.isPending;
  const mappings = mappingsQ.data?.mappings ?? [];
  const autoNamespaces = mappingsQ.data?.auto_namespaces ?? [];
  const conflicts = mappingsQ.data?.conflicts ?? [];
  const displayedConflicts = [
    ...conflicts.filter(
      (conflict) => !mutationConflicts.some((candidate) => candidate.alias === conflict.alias),
    ),
    ...mutationConflicts,
  ];
  const skus = useMemo(() => catalogQ.data?.skus ?? [], [catalogQ.data?.skus]);
  const namespaceGroups = useMemo(() => groupSkusByNamespace(skus), [skus]);
  const skuById = useMemo(() => {
    const map = new Map<string, CatalogSku>();
    for (const sku of skus) map.set(sku.sku_id, sku);
    return map;
  }, [skus]);

  function resetForm(): void {
    setEditingAlias(null);
    setAlias('');
    setSkuId('');
    setAliasError(null);
    setSkuError(null);
    setMutationConflicts([]);
  }

  function startEdit(mapping: ApiKeyModelMappingRecord): void {
    setEditingAlias(mapping.alias);
    setAlias(mapping.alias);
    setAliasError(null);
    setSkuError(null);
    setListError(null);
    setMutationConflicts([]);
    setSkuId(skuById.has(mapping.sku_id) ? mapping.sku_id : '');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setAliasError(null);
    setSkuError(null);
    setListError(null);

    const trimmedAlias = alias.trim();
    if (trimmedAlias.length === 0) {
      setAliasError(t('keys.mappings.alias_required'));
      return;
    }
    if (trimmedAlias.includes('/')) {
      setAliasError(t('keys.mappings.alias_no_slash'));
      return;
    }
    if (skuId.length === 0) {
      setSkuError(t('keys.mappings.sku_required'));
      return;
    }
    const isEdit = editingAlias !== null;
    setMutationConflicts([]);
    try {
      await upsertM.mutateAsync({ keyId: target.id, alias: trimmedAlias, skuId });
      toast.success(isEdit ? t('keys.mappings.toast.updated') : t('keys.mappings.toast.added'));
      resetForm();
    } catch (err) {
      if (err instanceof ModelMappingConflictError) {
        setMutationConflicts(err.conflicts);
        setAliasError(null);
        return;
      }
      // The platform's alias validation (no `/`, length) belongs to the
      // alias field; render it inline there instead of toast-only.
      const message = localizeCaughtError(err, t, 'keys.row_error.network');
      setAliasError(message);
    }
  }

  async function handleDeleteAlias(aliasToDelete: string): Promise<void> {
    setListError(null);
    try {
      await deleteM.mutateAsync({ keyId: target.id, alias: aliasToDelete });
      toast.success(t('keys.mappings.toast.deleted'));
      setMutationConflicts((current) =>
        current.filter((conflict) => conflict.alias !== aliasToDelete),
      );
      if (editingAlias === aliasToDelete) resetForm();
      if (deleteConflictAlias === aliasToDelete) setDeleteConflictAlias(null);
    } catch (err) {
      const message = localizeCaughtError(err, t, 'keys.row_error.network');
      setListError(message);
    }
  }

  function mappingTargetLabel(mapping: ApiKeyModelMappingRecord): string {
    // Catalog `sku_name` is brand-aliased for the Nxtpath brand; the
    // wire `sku_name` (canonical) is only a fallback for SKUs that have
    // left the org-visible catalog.
    return skuById.get(mapping.sku_id)?.sku_name ?? mapping.sku_name;
  }

  function sourceLabel(source: ApiKeyModelMappingRecord['source'] | undefined): string {
    return source === 'automatic'
      ? t('keys.mappings.source.automatic')
      : t('keys.mappings.source.manual');
  }

  function mappingSources(
    mapping: ApiKeyModelMappingRecord,
  ): readonly ApiKeyModelMappingRecord['source'][] {
    return mapping.sources?.length > 0 ? mapping.sources : [mapping.source ?? 'manual'];
  }

  function isManagedByAuto(mapping: ApiKeyModelMappingRecord): boolean {
    return mapping.managed_by_auto ?? mappingSources(mapping).includes('automatic');
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!pending) onClose();
      }}
      titleId="key-mappings-title"
      testId="keys-mappings-modal"
      variant="drawer"
    >
      <div className="flex h-full flex-col bg-card">
        <header className="border-b border-line px-6 py-5">
          <span className="font-mono text-mono-label uppercase text-rust-text">
            {t('keys.mappings.eyebrow')}
          </span>
          <h2 id="key-mappings-title" className="m-0 mt-1 font-display text-title text-ink">
            {t('keys.mappings.title')}
          </h2>
          <p className="m-0 mt-1 text-caption text-ink-low">
            {t('keys.mappings.subtitle', {
              key: target.name ?? (target.prefix.length > 0 ? `${target.prefix}…` : target.id),
            })}
          </p>
        </header>

        <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
          {mappingsQ.isLoading ? (
            <div className="flex flex-col gap-2" data-testid="keys-mappings-loading">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
              <span className="sr-only">{t('keys.mappings.loading')}</span>
            </div>
          ) : mappingsQ.isError ? (
            <div
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-bad-line bg-bad-tint px-4 py-3"
              role="alert"
              data-testid="keys-mappings-load-error"
            >
              <span className="text-body text-bad">
                {localizeCaughtError(mappingsQ.error, t, 'keys.mappings.error')}
              </span>
              <button
                type="button"
                onClick={() => {
                  void mappingsQ.refetch();
                }}
                className="inline-flex items-center justify-center rounded-lg bg-active px-3 py-1 text-mono-label font-medium uppercase text-ink"
              >
                {t('keys.mappings.retry')}
              </button>
            </div>
          ) : (
            <>
              <NamespaceMappingPanel
                keyId={target.id}
                canManage={canManage}
                groups={namespaceGroups}
                autoNamespaces={autoNamespaces}
                catalogUnavailable={catalogQ.isError}
                namespaceOperationsEnabled={mappingsQ.data?.namespace_operations_enabled !== false}
              />

              {displayedConflicts.length > 0 ? (
                <section
                  role="alert"
                  className="flex flex-col gap-3 rounded-lg border border-warn-line bg-warn-tint p-4"
                  data-testid="keys-mapping-conflicts"
                >
                  <div className="flex flex-col gap-1">
                    <h3 className="m-0 text-heading text-warn">
                      {t('keys.mappings.conflicts.title')}
                    </h3>
                    <p className="m-0 text-caption text-ink-mid">
                      {t('keys.mappings.conflicts.description')}
                    </p>
                  </div>
                  <ul className="m-0 flex list-none flex-col gap-2 p-0">
                    {displayedConflicts.map((conflict) => {
                      const isTransient = mutationConflicts.some(
                        (candidate) => candidate.alias === conflict.alias,
                      );
                      const hasPersistedManual =
                        !isTransient &&
                        conflict.candidates.some((candidate) =>
                          candidate.sources.includes('manual'),
                        );
                      return (
                        <li
                          key={conflict.alias}
                          className="rounded border border-warn-line bg-card px-3 py-2"
                          data-testid={`keys-mapping-conflict-${conflict.alias}`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="m-0 font-mono text-mono text-ink">{conflict.alias}</p>
                              <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-caption text-ink-mid">
                                {conflict.candidates.map((candidate) => (
                                  <li key={candidate.sku_id}>
                                    <span className="font-mono">
                                      {skuById.get(candidate.sku_id)?.sku_name ??
                                        candidate.sku_name}
                                    </span>
                                    {' · '}
                                    {candidate.sources
                                      .map((source) => sourceLabel(source))
                                      .join(', ')}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {canManage && hasPersistedManual ? (
                              <Button
                                variant="danger"
                                size="sm"
                                disabled={pending}
                                onClick={() => setDeleteConflictAlias(conflict.alias)}
                                data-testid={`keys-mapping-conflict-delete-${conflict.alias}`}
                              >
                                {t('keys.mappings.conflicts.delete_manual')}
                              </Button>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ) : null}

              {mappings.length === 0 ? (
                <p
                  className="m-0 rounded-lg border border-line bg-raised px-4 py-6 text-body text-ink-low"
                  data-testid="keys-mappings-empty"
                >
                  {t('keys.mappings.empty')}
                </p>
              ) : (
                <Table testId="keys-mappings-table">
                  <THead>
                    <Tr interactive={false}>
                      <Th>{t('keys.mappings.column.alias')}</Th>
                      <Th>{t('keys.mappings.column.target')}</Th>
                      <Th>{t('keys.mappings.column.source')}</Th>
                      {canManage ? <Th>{t('keys.column.actions')}</Th> : null}
                    </Tr>
                  </THead>
                  <TBody>
                    {mappings.map((mapping) => {
                      const automatic = isManagedByAuto(mapping);
                      return (
                        <Tr key={mapping.alias} data-testid={`keys-mapping-row-${mapping.alias}`}>
                          <Td mono emphasis heading>
                            {mapping.alias}
                          </Td>
                          <Td
                            mono
                            label={t('keys.mappings.column.target')}
                            data-testid={`keys-mapping-target-${mapping.alias}`}
                          >
                            {mappingTargetLabel(mapping)}
                          </Td>
                          <Td
                            label={t('keys.mappings.column.source')}
                            data-testid={`keys-mapping-source-${mapping.alias}`}
                          >
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 font-mono text-mono-label ${
                                automatic
                                  ? 'border-ok-line bg-ok-tint text-ok'
                                  : 'border-line bg-well text-ink-mid'
                              }`}
                            >
                              {mappingSources(mapping)
                                .map((source) => sourceLabel(source))
                                .join(' + ')}
                            </span>
                          </Td>
                          {canManage ? (
                            <Td label={t('keys.column.actions')} stack>
                              {automatic ? (
                                <span className="text-caption text-ink-low">
                                  {t('keys.mappings.automatic_managed')}
                                </span>
                              ) : (
                                <div className="flex flex-wrap items-center justify-end gap-2 max-md:justify-start">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={pending}
                                    onClick={() => startEdit(mapping)}
                                    data-testid={`keys-mapping-edit-${mapping.alias}`}
                                  >
                                    {t('keys.mappings.edit')}
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    disabled={pending}
                                    onClick={() => {
                                      void handleDeleteAlias(mapping.alias);
                                    }}
                                    data-testid={`keys-mapping-delete-${mapping.alias}`}
                                  >
                                    {t('keys.mappings.delete')}
                                  </Button>
                                </div>
                              )}
                            </Td>
                          ) : null}
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>
              )}
            </>
          )}

          {listError !== null ? (
            <p
              className="mt-3 text-caption font-medium text-bad"
              role="alert"
              data-testid="keys-mappings-list-error"
            >
              {listError}
            </p>
          ) : null}

          {canManage && !mappingsQ.isLoading && !mappingsQ.isError ? (
            <form
              noValidate
              onSubmit={(event) => {
                void handleSubmit(event);
              }}
              className="mt-6 flex flex-col gap-4 rounded-lg border border-line bg-raised p-4"
              data-testid="keys-mapping-form"
            >
              <p className="m-0 font-mono text-mono-label uppercase text-ink-low">
                {editingAlias !== null
                  ? t('keys.mappings.edit_title', { alias: editingAlias })
                  : t('keys.mappings.add_title')}
              </p>
              <label className="flex flex-col gap-2">
                <span className="font-mono text-mono-label uppercase text-ink-low">
                  {t('keys.mappings.alias_label')}
                </span>
                <input
                  type="text"
                  value={alias}
                  maxLength={200}
                  disabled={pending || editingAlias !== null}
                  aria-invalid={aliasError !== null || undefined}
                  placeholder={t('keys.mappings.alias_placeholder')}
                  onChange={(event) => {
                    setAlias(event.currentTarget.value);
                    setAliasError(null);
                  }}
                  data-testid="keys-mapping-alias"
                  className={INPUT_CLASS}
                />
                {aliasError !== null ? (
                  <span
                    className="text-mono-label font-medium leading-4 text-bad"
                    role="alert"
                    data-testid="keys-mapping-alias-error"
                  >
                    {aliasError}
                  </span>
                ) : (
                  <span className="text-mono-label leading-4 text-ink-low">
                    {t('keys.mappings.alias_hint')}
                  </span>
                )}
              </label>
              <label className="flex flex-col gap-2">
                <span className="font-mono text-mono-label uppercase text-ink-low">
                  {t('keys.mappings.sku_label')}
                </span>
                <select
                  value={skuId}
                  onChange={(event) => {
                    setSkuId(event.currentTarget.value);
                    setSkuError(null);
                  }}
                  disabled={pending || catalogQ.isLoading || catalogQ.isError}
                  data-testid="keys-mapping-sku"
                  className={INPUT_CLASS}
                >
                  <option value="">{t('keys.mappings.sku_placeholder')}</option>
                  {namespaceGroups.map((group) => (
                    <optgroup key={group.namespaceId} label={group.displayName}>
                      {group.skus.map((sku) => (
                        <option key={sku.sku_id} value={sku.sku_id}>
                          {sku.model_display_name ?? sku.model}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {skuError !== null ? (
                  <span
                    className="text-mono-label font-medium leading-4 text-bad"
                    role="alert"
                    data-testid="keys-mapping-sku-error"
                  >
                    {skuError}
                  </span>
                ) : null}
              </label>
              {catalogQ.isError ? (
                <p className="m-0 text-caption font-medium text-bad" role="alert">
                  {t('keys.mappings.catalog_error')}
                </p>
              ) : null}
              <div className="flex items-center justify-end gap-3">
                {editingAlias !== null ? (
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={pending}
                    data-testid="keys-mapping-cancel-edit"
                    className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-caption font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    {t('keys.mappings.cancel_edit')}
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={pending || catalogQ.isLoading}
                  data-testid="keys-mapping-submit"
                  className="inline-flex items-center justify-center rounded-lg bg-forest px-4 py-2 text-caption font-medium uppercase text-on-forest transition-all hover:bg-forest-bright disabled:opacity-40 disabled:pointer-events-none"
                >
                  {editingAlias !== null
                    ? t('keys.mappings.submit_save')
                    : t('keys.mappings.submit_add')}
                </button>
              </div>
            </form>
          ) : null}

          {deleteConflictAlias !== null ? (
            <Modal
              isOpen
              onClose={() => {
                if (!deleteM.isPending) setDeleteConflictAlias(null);
              }}
              titleId="delete-conflict-mapping-title"
              testId="keys-mapping-conflict-delete-confirm"
            >
              <div className="flex flex-col gap-5 p-6">
                <div className="flex flex-col gap-2">
                  <h2 id="delete-conflict-mapping-title" className="m-0 text-heading text-ink">
                    {t('keys.mappings.conflicts.delete_title')}
                  </h2>
                  <p className="m-0 text-body text-ink-mid">
                    {t('keys.mappings.conflicts.delete_description', {
                      alias: deleteConflictAlias,
                    })}
                  </p>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="secondary"
                    disabled={deleteM.isPending}
                    onClick={() => setDeleteConflictAlias(null)}
                  >
                    {t('keys.mappings.conflicts.delete_cancel')}
                  </Button>
                  <Button
                    variant="danger"
                    disabled={deleteM.isPending}
                    onClick={() => {
                      void handleDeleteAlias(deleteConflictAlias);
                    }}
                    data-testid="keys-mapping-conflict-delete-confirm-confirm"
                  >
                    {t('keys.mappings.conflicts.delete_confirm')}
                  </Button>
                </div>
              </div>
            </Modal>
          ) : null}
        </main>

        <footer className="flex items-center justify-end gap-3 border-t border-line px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            data-testid="keys-mappings-close"
            className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-caption font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('keys.mappings.close')}
          </button>
        </footer>
      </div>
    </Modal>
  );
}

interface PlaintextRevealProps {
  readonly plaintext: string;
  readonly keyName: string | null;
  readonly onClose: () => void;
}

function PlaintextReveal({ plaintext, keyName, onClose }: PlaintextRevealProps): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();
  const [hasCopied, setHasCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    const ok = await copy(plaintext);
    if (ok) {
      setHasCopied(true);
    }
  };

  const handlePrimaryAction = (): void => {
    if (hasCopied) {
      onClose();
      return;
    }
    void handleCopy();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="plaintext-reveal-title"
      testId="keys-plaintext-modal"
      closeOnBackdrop={false}
    >
      <div className="flex flex-col gap-6 p-8">
        <h2 id="plaintext-reveal-title" className="text-heading text-ink">
          {t('keys.plaintext.title')}
        </h2>
        <p
          className="rounded-lg border border-warn-line bg-warn-tint px-4 py-3 text-body font-medium text-ink"
          data-testid="keys-plaintext-warning"
          role="alert"
        >
          {t('keys.plaintext.warning')}
        </p>
        <button
          type="button"
          onClick={() => void handleCopy()}
          data-testid="keys-plaintext-copy"
          aria-label={t('keys.plaintext.copy_aria_label')}
          className="group flex flex-col items-start gap-2 rounded-lg bg-well p-4 text-left transition-all hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
        >
          <span className="text-[10px] uppercase text-ink-low">
            {keyName !== null && keyName.length > 0
              ? t('keys.plaintext.hint.click_with_name', { name: keyName })
              : t('keys.plaintext.hint.click_to_copy')}
          </span>
          <span
            data-testid="keys-plaintext-value"
            className="w-full break-all font-mono text-lg font-medium text-forest select-all"
          >
            {plaintext}
          </span>
          <span
            data-testid="keys-plaintext-copy-status"
            className={`text-[10px] uppercase ${copied ? 'text-rust-text' : 'text-ink-low'}`}
          >
            {copied ? t('keys.plaintext.copied') : t('keys.plaintext.copy_anywhere')}
          </span>
        </button>
        {error !== null ? (
          <p
            className="text-mono-label font-medium text-bad"
            role="alert"
            data-testid="keys-plaintext-copy-error"
          >
            {t(clipboardErrorKey(error))}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="keys-plaintext-close"
            className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-caption font-medium uppercase text-ink transition-colors hover:bg-active"
          >
            {t('keys.plaintext.close')}
          </button>
          <button
            type="button"
            onClick={handlePrimaryAction}
            data-testid="keys-plaintext-done"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-forest px-4 py-2 text-caption font-medium uppercase text-on-forest transition-all hover:bg-forest-bright"
          >
            <span aria-hidden="true" className="inline-flex">
              {hasCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </span>
            <span>{hasCopied ? t('keys.plaintext.done') : t('keys.plaintext.copy_button')}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface RevokeKeyModalProps {
  readonly target: ApiKeySummary;
  readonly isPending: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => Promise<string | null>;
}

/**
 * Deletion runs through the shared `ConfirmDialog` with its
 * type-to-confirm gate: the confirm button stays disabled until the
 * operator retypes the key's name (prefix for unnamed keys —
 * `apiKeyRevokePhrase`, the same rule console-web's `/keys` applies).
 * A rejected DELETE reports inline and keeps the dialog open.
 */
function RevokeKeyModal({
  target,
  isPending,
  onClose,
  onConfirm,
}: RevokeKeyModalProps): JSX.Element {
  const t = useT();
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setError(null);
    const message = await onConfirm();
    if (message !== null) {
      setError(message);
    }
  }

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
      tone="danger"
      title={t('keys.revoke_modal.title')}
      body={
        <div className="flex flex-col gap-2">
          <p className="m-0">
            {t('keys.revoke_modal.description.before')}
            <span className="font-mono font-medium text-ink">
              {target.prefix.length > 0 ? `${target.prefix}…` : target.id}
            </span>
            {t('keys.revoke_modal.description.after')}
          </p>
          {error !== null ? (
            <p
              className="m-0 text-caption font-medium text-bad"
              role="alert"
              data-testid="keys-revoke-error"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={isPending ? t('keys.revoke_modal.submitting') : t('keys.revoke_modal.submit')}
      cancelLabel={t('keys.revoke_modal.cancel')}
      closeLabel={t('keys.revoke_modal.close')}
      typeToConfirm={apiKeyRevokePhrase(target.name, target.prefix)}
      typeToConfirmLabel={t('keys.revoke_modal.type_label')}
      loading={isPending}
      testId="keys-revoke-modal"
      onConfirm={() => {
        void handleConfirm();
      }}
    />
  );
}
